import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableCell,
  TableRow,
  WidthType,
  AlignmentType,
  HeadingLevel,
  BorderStyle,
  Header,
  Footer,
  PageNumber
} from "docx";
import { saveAs } from "file-saver";

export const exportCircularityAsDocx = async ({
  productName,
  code,
  today,
  mats,
  totalMass,
  virginMass,
  recycledInflow,
  recycledOut,
  lossOut,
  projectMci,
  gwpSum,
  boundary,
  serviceLife,
  weightedMci,
  renewablePct,
  topic = "Product"
}) => {
  const excludedSet = new Set([
    "building service engineering",
    "processes at building site",
    "end of life",
    "energy sources",
    "transport",
    "chemicals",
    "refrigerants",
    "water"
  ]);

  const parseNumericLocal = (val, defaultVal = 0) => {
    if (val === undefined || val === null) return defaultVal;
    if (typeof val === "number") return val;
    const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const filteredMats = mats.filter(m => {
    const typeVal = String(m.type || "").toLowerCase().trim();
    return !excludedSet.has(typeVal);
  });

  let sumPci = 0;
  let sumFi = 0;
  let sumPciTimesMass = 0;
  let totalDesignMass = 0;
  let validCount = 0;

  const designRows = filteredMats.map((m) => {
    const ct = m.ct_factor !== undefined && m.ct_factor !== null ? parseNumericLocal(m.ct_factor, 0.8) : 0.8;
    const ca = m.ca_factor !== undefined && m.ca_factor !== null ? parseNumericLocal(m.ca_factor, 0.8) : 0.8;
    const indep = m.indep_factor !== undefined && m.indep_factor !== null ? parseNumericLocal(m.indep_factor, 0.4) : 0.4;
    const gpe = m.gpe_factor !== undefined && m.gpe_factor !== null ? parseNumericLocal(m.gpe_factor, 0.4) : 0.4;
    
    const mciVal = m.percentage_mci !== undefined
      ? Number(m.percentage_mci)
      : (m.MCI !== undefined
        ? Math.max(0, (Number(m.MCI) - 0.1) / 0.9)
        : (m.row_mci !== undefined ? Math.max(0, (Number(m.row_mci) - 0.1) / 0.9) : 0.0));
    const fi = ct * ca * indep * gpe;
    const pci = mciVal * fi;
    const mass = m.mass || 0;

    sumPci += pci;
    sumFi += fi;
    sumPciTimesMass += pci * mass;
    totalDesignMass += mass;
    validCount++;

    return [
      m.description,
      mciVal.toFixed(2),
      ct.toFixed(2),
      ca.toFixed(2),
      indep.toFixed(2),
      gpe.toFixed(2),
      pci.toFixed(2)
    ];
  });

  const overallPciConnectionWeighted = validCount > 0 ? sumPci / validCount : 0.0;
  const overallPciMassWeighted = totalDesignMass > 0 ? sumPciTimesMass / totalDesignMass : 0.0;
  const overallDisassemblyPotential = validCount > 0 ? sumFi / validCount : 0.0;

  // Report copy is authored around "Product" — this word-boundary swap lets
  // the same export serve Project/Building circularity reports.
  const renameProductWord = (str) => {
    if (typeof str !== "string" || topic === "Product") return str;
    return str
      .replace(/\bProducts\b/g, `${topic}s`)
      .replace(/\bProduct\b/g, topic)
      .replace(/\bproducts\b/g, `${topic.toLowerCase()}s`)
      .replace(/\bproduct\b/g, topic.toLowerCase());
  };

  const headingStyle = (text, size = 32) => {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          bold: true,
          color: "4C7F25",
          font: "Poppins",
          size
        })
      ],
      spacing: { before: 240, after: 120 }
    });
  };

  const subheadingStyle = (text) => {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          bold: true,
          color: "111827",
          font: "Poppins",
          size: 24
        })
      ],
      spacing: { before: 180, after: 80 }
    });
  };

  const bodyStyle = (text, options = {}) => {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          font: "Poppins",
          size: 20,
          ...options
        })
      ],
      spacing: { after: 120 }
    });
  };

  const chartFont = "Poppins";
  const createBarFillRow = (percent, color) => {
    const filled = Math.max(1, Math.round((percent / 100) * 20));
    const cells = [];

    for (let index = 0; index < 20; index += 1) {
      cells.push(
        new TableCell({
          children: [new Paragraph({ text: "", spacing: { after: 0 } })],
          shading: { fill: index < filled ? color : "E5E7EB" },
          margins: { top: 30, bottom: 30, left: 0, right: 0 },
          width: { size: 5, type: WidthType.PERCENTAGE }
        })
      );
    }

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [new TableRow({ children: cells })],
      borders: {
        top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
      }
    });
  };

  const createHorizontalBarChartImage = (title, labels, values, options = {}) => {
    const maxValue = Math.max(...values, 1);
    const rows = labels.map((label, index) => {
      const value = Number(values[index] || 0);
      const percent = (value / maxValue) * 100;
      return new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: String(label), font: chartFont, size: 18, color: "111827" })],
                spacing: { after: 0 }
              })
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 }
          }),
          new TableCell({
            children: [
              createBarFillRow(percent, options.barColor || "4C7F25"),
              new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 40, after: 0 }, children: [new TextRun({ text: `${value.toFixed(2)} kg`, font: chartFont, size: 16, color: "374151" })] })
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 }
          })
        ]
      });
    });

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: title, font: chartFont, size: 24, bold: true, color: "111827" })],
                  spacing: { after: 80 }
                }),
                ...(options.subtitle
                  ? [new Paragraph({ children: [new TextRun({ text: options.subtitle, font: chartFont, size: 14, color: "6B7280" })], spacing: { after: 120 } })]
                  : [])
              ],
              columnSpan: 2,
              shading: { fill: "FFFFFF" },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
              }
            })
          ]
        }),
        ...rows
      ],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "F3F4F6" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
      }
    });
  };

  const createDonutChartImage = (title, segments) => {
    const total = segments.reduce((sum, segment) => sum + Number(segment.value || 0), 0) || 1;
    const legendRows = segments.map((segment, index) => {
      const value = Number(segment.value || 0);
      const pct = Math.round((value / total) * 100);
      const color = ["E86D6D", "E571D8", "6B4DE6", "6FAEE2", "76E0B2", "D6C54E", "A5D63A", "3CCFA7"][index % 8];

      return new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: String(segment.name), font: chartFont, size: 18, color: "111827" })],
                spacing: { after: 0 }
              })
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 }
          }),
          new TableCell({
            children: [
              createBarFillRow(pct, color),
              new Paragraph({
                children: [new TextRun({ text: `${pct}% / ${value.toFixed(2)} kg`, font: chartFont, size: 16, color: "374151" })],
                alignment: AlignmentType.RIGHT,
                spacing: { before: 40, after: 0 }
              })
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 }
          })
        ]
      });
    });

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: title, font: chartFont, size: 24, bold: true, color: "111827" })],
                  spacing: { after: 80 }
                }),
                new Paragraph({
                  children: [new TextRun({ text: "Segmented by material mass contribution", font: chartFont, size: 14, color: "6B7280" })],
                  spacing: { after: 120 }
                })
              ],
              columnSpan: 2,
              shading: { fill: "FFFFFF" },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
              }
            })
          ]
        }),
        ...legendRows
      ],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "F3F4F6" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" }
      }
    });
  };

  // Helper to create beautiful tables with Grid Table 4 - Accent 2 borders and backgrounds
  const createTable = (headers, rowsData) => {
    const headerRow = new TableRow({
      children: headers.map(
        (header) =>
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: String(header),
                    bold: true,
                    color: "FFFFFF",
                    font: "Poppins",
                    size: 20
                  })
                ],
                alignment: AlignmentType.LEFT
              })
            ],
            shading: { fill: "4C7F25" },
            margins: { top: 100, bottom: 100, left: 150, right: 150 }
          })
      )
    });

    const dataRows = rowsData.map((row, idx) =>
      new TableRow({
        children: row.map(
          (cell) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: String(cell ?? ""),
                      font: "Poppins",
                      size: 20,
                      color: "171717"
                    })
                  ],
                  alignment: AlignmentType.LEFT
                })
              ],
              shading: { fill: idx % 2 === 0 ? "EBF1DE" : "F5F9F0" },
              margins: { top: 100, bottom: 100, left: 150, right: 150 }
            })
        )
      })
    );

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: "4C7F25" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "4C7F25" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "4C7F25" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "4C7F25" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "EBF1DE" },
        insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "EBF1DE" }
      }
    });
  };

  // Helper to create visual metric cards as a 2x2 grid table
  const createMetricCardsGrid = (cards) => {
    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: "fixed",
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 60 },
                  children: [new TextRun({ text: cards[0].title, bold: true, size: 18, color: "FFFFFF", font: chartFont })]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: cards[0].value, size: 30, bold: true, color: "FFFFFF", font: chartFont })]
                })
              ],
              shading: { fill: "4C7F25" },
              margins: { top: 220, bottom: 220, left: 180, right: 180 }
            }),
            new TableCell({
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 60 },
                  children: [new TextRun({ text: cards[1].title, bold: true, size: 18, color: "FFFFFF", font: chartFont })]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: cards[1].value, size: 30, bold: true, color: "FFFFFF", font: chartFont })]
                })
              ],
              shading: { fill: "608A37" },
              margins: { top: 220, bottom: 220, left: 180, right: 180 }
            })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 60 },
                  children: [new TextRun({ text: cards[2].title, bold: true, size: 18, color: "FFFFFF", font: chartFont })]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: cards[2].value, size: 30, bold: true, color: "FFFFFF", font: chartFont })]
                })
              ],
              shading: { fill: "608A37" },
              margins: { top: 220, bottom: 220, left: 180, right: 180 }
            }),
            new TableCell({
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 60 },
                  children: [new TextRun({ text: cards[3].title, bold: true, size: 18, color: "FFFFFF", font: chartFont })]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: cards[3].value, size: 30, bold: true, color: "FFFFFF", font: chartFont })]
                })
              ],
              shading: { fill: "4C7F25" },
              margins: { top: 220, bottom: 220, left: 180, right: 180 }
            })
          ]
        })
      ],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" },
        bottom: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" },
        left: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" },
        right: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" },
        insideVertical: { style: BorderStyle.SINGLE, size: 6, color: "FFFFFF" }
      }
    });
  };

  // Helper to create visual charts represented as formatted layout tables
  const createVisualChartPlaceholder = (title, barLabels, barValues) => {
    return createHorizontalBarChartImage(title, barLabels, barValues, { subtitle: "Normalized by the largest value in the set" });
  };

  // Helper to create a visual Pie Chart substitute as a structured table
  const createPieChartPlaceholder = (title, segments) => {
    return createDonutChartImage(title, segments);
  };

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: {
              ascii: "Poppins",
              cs: "Poppins",
              eastAsia: "Poppins",
              hAnsi: "Poppins"
            }
          }
        }
      }
    },
    sections: [
      {
        properties: {
          page: {
            shading: { fill: "F5F9F0" } // Minimal green page background matching BRSR word doc
          }
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Arivu Circularity Report  |  ${productName}`,
                    font: "Poppins",
                    size: 18,
                    color: "6B7280"
                  })
                ],
                alignment: AlignmentType.RIGHT,
                spacing: { after: 120 }
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Page ",
                    font: "Poppins",
                    size: 18,
                    color: "6B7280"
                  }),
                  PageNumber.CURRENT,
                  new TextRun({
                    text: " of ",
                    font: "Poppins",
                    size: 18,
                    color: "6B7280"
                  }),
                  PageNumber.TOTAL_PAGES
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 120 }
              })
            ]
          })
        },
        children: [
          // Cover Page
          new Paragraph({ text: "", spacing: { before: 1200 } }),
          new Paragraph({
            children: [
              new TextRun({
                text: "Circularity Report",
                bold: true,
                color: "4C7F25",
                size: 24,
                font: "Poppins"
              })
            ]
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: productName,
                bold: true,
                color: "111827",
                size: 72,
                font: "Poppins"
              })
            ],
            spacing: { before: 240, after: 480 }
          }),
          new Paragraph({ text: "", spacing: { before: 1200 } }),
          
          createTable(
            ["Configuration Metadata", "Setting Details"],
            [
              ["Framework", "ISO 59040:2025 aligned"],
              ["Date Generated", today],
              ["Prepared By", "ARIVU CIRCULARITY ENGINE"]
            ]
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("Table of Contents", 36),
          bodyStyle("Summary"),
          bodyStyle(renameProductWord("1. Product Information")),
          bodyStyle("2. Material Composition"),
          bodyStyle("3. Circularity Inputs & Outputs"),
          bodyStyle("4. Utility"),
          bodyStyle(renameProductWord("5. Material Circularity Indicator")),
          bodyStyle("6. Design Assessment (PCI)"),
          bodyStyle("7. Analytics"),
          bodyStyle("Overall Circularity Performance Summary"),
          bodyStyle("Appendix"),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("Summary"),
          bodyStyle(
            renameProductWord(`${productName} achieves a Material Circularity Indicator score of ${projectMci.toFixed(
              2
            )}% and ${totalMass.toFixed(2)} kg of total mass, with a cradle-to-gate embodied carbon of ${gwpSum.toFixed(2)} kg CO2e. The product outperforms the industry average and is positioned as an emerging circularity leader.`)
          ),
          bodyStyle(
            "Key strengths are long service life, high steel and secondary-aluminium content, and a renewable-based insulation core. Primary opportunities relate to virgin aluminium substitution, formal recovery of coated components and upgradeability documentation."
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle(renameProductWord("1. Product Information")),
          createTable(
            ["Indicator Property", "Factual Value"],
            [
              [renameProductWord("Product Name"), productName],
              [renameProductWord("Product ID"), code || "—"],
              ["Functional Unit", `${serviceLife} years service life`],
              [renameProductWord("Total product mass"), `${totalMass.toFixed(2)} kg`],
              [renameProductWord("Material Circularity Indicator"), `${projectMci.toFixed(2)}%`],
              ["Standards followed", "ISO 59020:2024, ISO 59040:2025, EN 15804+A2"]
            ]
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("2. Material Composition"),
          subheadingStyle("Composition KPI Summary"),
          createMetricCardsGrid([
            { title: "Virgin", value: `${virginMass.toFixed(2)} kg (${totalMass > 0 ? ((virginMass / totalMass) * 100).toFixed(2) : "0.00"}%)` },
            { title: "Recycled", value: `${recycledInflow.toFixed(2)} kg (${totalMass > 0 ? ((recycledInflow / totalMass) * 100).toFixed(2) : "0.00"}%)` },
            { title: "Reused", value: `${(mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_reuse || 0) / 100), 0)).toFixed(2)} kg (${totalMass > 0 ? (((mats.reduce((s, m) => s + (m.mass || 0) * ((m.im_reuse || 0) / 100), 0)) / totalMass) * 100).toFixed(2) : "0.00"}%)` },
            { title: "Total Mass", value: `${totalMass.toFixed(2)} kg` },
            { title: "Total GWP", value: `${gwpSum.toFixed(2)} kg CO2e` }
          ]),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          subheadingStyle("Material Breakdown"),
          createTable(
            ["Material", "Category", "Mass (kg)", "% Mass", "GWP (kg CO2e)"],
            mats.map((m) => [
              m.description,
              m.category || "—",
              (m.mass || 0).toFixed(2),
              totalMass > 0 ? ((m.mass || 0) / totalMass * 100).toFixed(2) + "%" : "0.00%",
              (m.gwp_total || 0).toFixed(2)
            ])
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("3. Circularity Inputs & Outputs"),
          bodyStyle(
            "This section presents the physical flows consolidated from the circularity database records. Material inflows map the raw feedstock inputs (virgin vs. recycled ratio), and outflows track expected recovery pathways (recycling efficiency or end-of-life collection potential)."
          ),
          subheadingStyle("Inflows"),
          createTable(
            ["Parameter", "Weight"],
            [
              ["Primary (virgin)", `${virginMass.toFixed(2)} kg`],
              ["Secondary (recycled)", `${recycledInflow.toFixed(2)} kg`],
              ["Total Inflow", `${totalMass.toFixed(2)} kg`]
            ]
          ),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          subheadingStyle("Outflows"),
          createTable(
            ["Parameter", "Weight"],
            [
              ["Recovered output", `${recycledOut.toFixed(2)} kg`],
              ["Waste / loss", `${lossOut.toFixed(2)} kg`],
              ["Recovery rate", totalMass > 0 ? `${(recycledOut / totalMass * 100).toFixed(2)}%` : "—"]
            ]
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("Life Outflow Distribution"),
          await createVisualChartPlaceholder(
            "Life Outflow Distribution",
            ["Virgin inflow", "Recycled inflow", "Recovered output", "Loss / waste"],
            [+virginMass.toFixed(2), +recycledInflow.toFixed(2), +recycledOut.toFixed(2), +lossOut.toFixed(2)]
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("4. Utility"),
          createTable(
            ["Lifetime Attribute", "Setting Value"],
            [
              ["Reference lifetime", `${serviceLife} years`],
              ["Actual lifetime", `${serviceLife} years`],
              ["Maintenance frequency", "Annual visual inspection"],
              ["Repairability", "High"],
              ["Upgradeability", "Medium"],
              ["Reusability", "High"]
            ]
          ),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          subheadingStyle("Per-material Utility"),
          createTable(
            ["Material", "Life (yrs)", "Reference (yrs)", "Utility factor"],
            mats.map((m) => {
              const lifeVal = m.uf_product_life !== undefined && m.uf_product_life !== null && !isNaN(Number(m.uf_product_life))
                ? Number(m.uf_product_life)
                : serviceLife;
              const indVal = m.uf_industry_avg_life !== undefined && m.uf_industry_avg_life !== null && !isNaN(Number(m.uf_industry_avg_life))
                ? Number(m.uf_industry_avg_life)
                : serviceLife;
              const ufVal = m.uf_calculations !== undefined && m.uf_calculations !== null && !isNaN(Number(m.uf_calculations))
                ? Number(m.uf_calculations)
                : (indVal > 0 ? lifeVal / indVal : 1.0);
              return [
                m.description,
                lifeVal.toFixed(2),
                indVal.toFixed(2),
                ufVal.toFixed(2)
              ];
            })
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("End-of-Life Outflow Distribution"),
          await createVisualChartPlaceholder(
            "End-of-Life Outflow Distribution",
            ["Recycled/Recovered", "Waste/Loss"],
            [+recycledOut.toFixed(2), +lossOut.toFixed(2)]
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle(renameProductWord("5. Material Circularity Indicator")),
          subheadingStyle("Material Circularity Indicator Breakdown"),
          createTable(
            ["Material", "Virgin %", "Recycled %", "Recycling eff.", "MCI"],
            mats.map((m) => [
              m.description,
              `${(m.im_virgin_content || 0).toFixed(2)}%`,
              `${(m.im_recycled_content || 0).toFixed(2)}%`,
              `${(m.eol_recycling_efficiency || 0).toFixed(2)}%`,
              ((m.row_mci || 0) * 100).toFixed(2) + "%"
            ])
          ),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          subheadingStyle("Material Circularity Indicator Summary"),
          bodyStyle(renameProductWord(`Overall Material Circularity Indicator: ${projectMci.toFixed(2)}%. Weighted-average score across all materials.`)),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("6. Design Assessment (PCI)"),
          bodyStyle(
            "The Design Assessment integrates Design for Disassembly (DfD) principles to evaluate the structural ease of separation, repair, and component harvesting. Connection Type (CT), Accessibility (CA), Independency (ID), and Geometry of Product Edge (GPE) are combined with per-material circularity (MCIp) to calculate the Product Circularity Indicator (PCI)."
          ),
          subheadingStyle("Connection and Disassembly Factors Breakdown"),
          createTable(
            ["Description", "MCIp", "CT", "CA", "ID", "GPE", "PCI"],
            designRows
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("7. Analytics Summary"),
          bodyStyle(
            "The Analytics module synthesizes physical material flows, embodied carbon intensity, and circularity indicators into a consolidated executive overview."
          ),
          createMetricCardsGrid([
            { title: "Material Circularity Indicator", value: `${projectMci.toFixed(2)}%` },
            { title: "Product Circularity Indicator", value: `${(overallPciMassWeighted * 100).toFixed(2)}%` },
            { title: "Embodied Carbon", value: `${gwpSum.toFixed(2)} kg CO2e` },
            { title: "Recovery Rate", value: totalMass > 0 ? `${(recycledOut / totalMass * 100).toFixed(2)}%` : "0.00%" }
          ]),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          await createPieChartPlaceholder(
            "Material Contribution Distribution",
            mats.map((m) => ({ name: m.description, value: m.mass || 0 }))
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("Overall Circularity Performance Summary"),
          createTable(
            ["Performance Indicator", "Factual Result"],
            [
              ["Material Circularity Indicator (MCI)", `${projectMci.toFixed(2)}%`],
              ["Product Circularity Indicator (PCI)", `${(overallPciMassWeighted * 100).toFixed(2)}%`],
              ["Weighted MCI", weightedMci.toFixed(2)],
              ["Disassembly Potential (Fd)", overallDisassemblyPotential.toFixed(2)],
              ["Recovery Efficiency", `${(totalMass > 0 ? (recycledOut / totalMass * 100) : 0).toFixed(2)}%`],
              ["Renewable Content", `${renewablePct.toFixed(2)}%`]
            ]
          ),
          new Paragraph({ text: "", spacing: { before: 180 } }),
          bodyStyle(
            "Overall performance demonstrates strong circular economy potential with long service life and high secondary feedstock utilization. Implementing targeted disassembly optimizations and secondary material substitutions will position the assembly in the top industry quartile."
          ),

          // Page Break
          new Paragraph({ text: "", pageBreakBefore: true }),
          headingStyle("Appendix"),
          subheadingStyle("Calculation assumptions"),
          bodyStyle(renameProductWord("• Mass balance normalised at product functional unit.")),
          bodyStyle("• Recycling efficiency uses material-specific factors from Ecoinvent 3.10."),
          bodyStyle("• Utility factor computed as (L / L_avg) × (U / U_avg) per EMF MCI methodology."),
          
          subheadingStyle("Methodology"),
          bodyStyle(
            "Ellen MacArthur Foundation MCI v1.0 methodology; ISO 59020:2024 indicator set; ISO 59040:2025 PCDS payload conventions."
          ),
          
          subheadingStyle("Definitions"),
          bodyStyle("• MCI: Material Circularity Indicator (0–1)."),
          bodyStyle("• LFI: Linear Flow Index (0–1); lower is more circular."),
          bodyStyle(renameProductWord("• Utility factor: Relative product usefulness vs industry reference.")),
          
          subheadingStyle("Acronyms"),
          bodyStyle(
            "GWP – Global Warming Potential • PCDS – Product Circularity Data Sheet • EMF – Ellen MacArthur Foundation • EPD – Environmental Product Declaration."
          ),
          
          subheadingStyle("Data quality notes"),
          bodyStyle(
            "Primary data used for aluminium and steel inputs; secondary/database data for insulation and coatings. Overall confidence: medium-high."
          ),
          
          subheadingStyle("ISO references"),
          bodyStyle("ISO 59020:2024, ISO 59040:2025, ISO 14040/44:2006, EN 15804+A2:2019.")
        ]
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${productName || code || "product"}-circularity-report.docx`);
};
